// Payslip arithmetic, mirroring the backend (ems-backend
// backend/helper/PayslipGenerator.php::build and
// ApiController::actionPayslipCalculation) so a figure edited on the form
// moves the figures that depend on it the same way the generator would.
//
// Every input is a form string. An empty field counts as 0; anything that
// is not a number makes the function return null so the caller keeps the
// last good figures while the user is mid-typing ("1e", "-").

const toNumber = (value) => {
  if (value === undefined || value === null) return 0;
  const text = String(value).trim();
  if (text === "") return 0;
  const n = Number(text);
  return Number.isFinite(n) ? n : NaN;
};

const readAll = (values) => {
  const out = {};
  for (const key of Object.keys(values)) {
    const n = toNumber(values[key]);
    if (Number.isNaN(n)) return null;
    out[key] = n;
  }
  return out;
};

export const money = (value) =>
  (Math.round((value + Number.EPSILON) * 100) / 100).toFixed(2);

// Day counts can be halves; drop float noise without forcing decimals.
const days = (value) => String(Math.round(value * 100) / 100);

// basic = salary / 2; the rest are shares of basic.
export const bifurcation = (salary) => {
  const n = readAll({ salary });
  if (!n) return null;
  const basic = n.salary / 2;
  return {
    basic_bifurcation: money(basic),
    house_rent: money(basic * 0.23),
    conveyance_allowance: money(basic * 0.12),
    medical_allowance: money(basic * 0.1),
    internet_phone_allowance: money(basic * 0.05),
    other_bifurcation: money(basic * 0.5),
  };
};

// in hand = salary - salary / working days in the month * unpaid leave days
export const inHandSalary = ({ salary, workDays, unpaidDays }) => {
  const n = readAll({ salary, workDays, unpaidDays });
  if (!n) return null;
  const deduction = n.workDays > 0 ? (n.salary / n.workDays) * n.unpaidDays : 0;
  return money(n.salary - deduction);
};

// credited = in hand - (deduction % + tax %) of in hand
//            + extra-working bonus + random bonus - PF - ESI
// The generator writes tax, PF, ESI and both bonuses as 0, so for a
// generated slip this is exactly its "in hand - deduction %".
export const creditedSalary = ({
  inHand,
  deductionPercent,
  taxPercent,
  pf,
  esi,
  extraBonus,
  randomBonus,
}) => {
  const n = readAll({
    inHand,
    deductionPercent,
    taxPercent,
    pf,
    esi,
    extraBonus,
    randomBonus,
  });
  if (!n) return null;
  const percent = (n.deductionPercent + n.taxPercent) / 100;
  return money(
    n.inHand * (1 - percent) + n.extraBonus + n.randomBonus - n.pf - n.esi
  );
};

// total leaves = covered by balance + without pay;
// worked days  = working days in the month - total leaves
export const leaveTotals = ({ workDays, freeDays, unpaidDays }) => {
  const n = readAll({ workDays, freeDays, unpaidDays });
  if (!n) return null;
  const total = n.freeDays + n.unpaidDays;
  return { total_leaves: days(total), total_working_days: days(n.workDays - total) };
};

export const workedDays = ({ workDays, totalLeaves }) => {
  const n = readAll({ workDays, totalLeaves });
  if (!n) return null;
  return days(n.workDays - n.totalLeaves);
};
